import {
    BasicResponsiveImage,
    Box,
    Card,
    FlexBox,
    H2,
    Li,
    Typography,
    Ul,
    tokensRaw,
} from '@vp/swan';
import styled from 'styled-components';
import { __ } from '@99designs/i18n';
import { Price } from '@99designs/pricing';
import { NormalisedBriefProduct } from '../BriefContext';
import StartingProjectDisplay from '../StartingProjectDisplay/StartingProjectDisplay';
import { BriefWorkEntityField } from './FormFields/WorkEntityField';
import { Field_WorkEntityField_Fragment } from './brief.generated';
import type { InvitedDesigner } from './InvitedDesigner';

const AvatarWrapper = styled(Box)`
    min-width: 32px;
    width: 32px;
`;

export interface WorkEntityDisplay {
    title: string;
    description: string | null;
    field: Field_WorkEntityField_Fragment;
}

export interface LogomakerAsset {
    id: string | null | undefined;
    sig: string | null | undefined;
}

export interface StepProductCardProps {
    product: NormalisedBriefProduct;
    startingAsset?: WorkEntityDisplay | LogomakerAsset;
    invitedDesigner?: InvitedDesigner | null;
}

export function StepProductCard({ product, startingAsset, invitedDesigner }: StepProductCardProps) {
    const isStartingAssetWorkEntity = startingAsset && 'field' in startingAsset;
    const isStartingAssetLogomker = startingAsset && 'id' in startingAsset;

    function StartingAssetDisplay() {
        if (isStartingAssetWorkEntity) {
            return (
                <Box mt={4}>
                    <BriefWorkEntityField
                        workEntityValue={startingAsset.field.workEntityValue}
                        title={startingAsset.title}
                        description={startingAsset.description}
                    />
                </Box>
            );
        }

        if (isStartingAssetLogomker) {
            return (
                <Box mt={4}>
                    <StartingProjectDisplay
                        images={[
                            {
                                url: `https://api.sherbert.cimpress.io/v2/assets/${startingAsset.id}?signature=${startingAsset.sig}`,
                            },
                        ]}
                        title={__('Your Starting Project', {
                            _context:
                                'Title for Logomaker starting project Image, which is used as a reference for designers',
                        })}
                        description={__(
                            'Your designer will use your Logomaker project as a starting point.',
                            {
                                _context:
                                    'Description for Logomaker starting project image, which is used as a reference for designers',
                            }
                        )}
                    />
                </Box>
            );
        }

        return null;
    }

    return (
        <Card
            bordered
            p="5"
            style={{
                minHeight: '96px',
                borderRadius: `${tokensRaw.SwanSemBorderRadiusStandard}`,
                width: '100%',
            }}
        >
            <FlexBox alignItems="center" justifyContent="space-between" flexWrap="wrap-reverse">
                <FlexBox justifyContent="space-between" style={{ width: '100%' }}>
                    <Box>
                        <H2 fontSize="large" fontWeight="bold" textAlign="left" pr={8} mb={0}>
                            {product.title}
                        </H2>
                        <Ul mt={4}>
                            {product.details.map((item) => (
                                <Li key={item.title}>{item.title}</Li>
                            ))}
                        </Ul>
                    </Box>
                    <Typography fontSize="large" fontWeight="bold" textAlign="right" mb={0} ml={5}>
                        <Price
                            price={{
                                currency: product.pricing.currency,
                                listPrice: {
                                    taxed: product.pricing.taxed,
                                    untaxed: product.pricing.untaxed,
                                },
                            }}
                            subtleTaxMessage
                            stacked
                        />
                    </Typography>
                </FlexBox>
            </FlexBox>
            {invitedDesigner && (
                <Box
                    mt={4}
                    p={3}
                    style={{
                        backgroundColor: tokensRaw.SwanSemColorBgStrong,
                        borderRadius: tokensRaw.SwanSemBorderRadiusStandard,
                    }}
                >
                    <FlexBox alignItems="center">
                        <AvatarWrapper mr="3">
                            <BasicResponsiveImage
                                src={invitedDesigner.profile.avatarUrl}
                                alt={invitedDesigner.displayName}
                                aspectRatio={1}
                                style={{ borderRadius: '50%' }}
                            />
                        </AvatarWrapper>
                        <Typography fontSkin="body-standard" fontWeight="bold">
                            {__('Designed by {{designer}}', {
                                designer: invitedDesigner.displayName,
                            })}
                        </Typography>
                    </FlexBox>
                    <Typography fontSkin="body-small" textColor="subtle" mt={3} mb={0}>
                        {__(
                            "Please note: The 24 hour turnaround time starts once the designer accepts your invitation. We'll match you with someone else if this designer is not available.",
                            {
                                _context:
                                    'Note shown to customers when a specific designer has been invited to work on their brief',
                            }
                        )}
                    </Typography>
                </Box>
            )}
            <StartingAssetDisplay />
        </Card>
    );
}
